import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';

// Shared customer databases — a partner organiser's people, linked to a client
// for SEGMENT BUILDING only (server/sharedDatabases.js).
//
// Dual-surface via the `scope` prop, but deliberately asymmetric:
//   'admin-client' (Admin → the client → Shared databases) — link, relabel, revoke.
//   'my'           (Settings → Shared databases)              — READ what you hold.
// Granting access to somebody else's customer database is not a client's call to
// make: it would be self-elevation past the organiser boundary the whole platform
// rests on. So client self-service here is "know exactly what you have and what it
// lets you do", with a clear line on who to ask to change it.
//
// Mobile-first: one stacked column, ≥40px controls, nothing side by side.
export default function SharedDatabasesCard({ entityId, scope = 'my' }) {
  const isAdmin = scope === 'admin-client';
  const [view, setView] = useState(null);
  const [denied, setDenied] = useState(false);
  // The organiser picker searches BOTH sources: the organisers Pulse already
  // scopes a client to, and (live, from the warehouse) every other organiser —
  // so a partner who isn't a Pulse client is found by name rather than typed from
  // memory, where a typo silently resolves to nobody.
  const [picked, setPicked] = useState([]);     // organiser names ticked
  const [search, setSearch] = useState('');
  const [found, setFound] = useState(null);     // warehouse lookup result | null = not run
  const [findBusy, setFindBusy] = useState(false);
  const [findErr, setFindErr] = useState('');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  // Narrowing an EXISTING link to specific events — the other half of granting
  // several at once, which necessarily links each one whole-database.
  const [editing, setEditing] = useState(null);   // grant id | null
  const [editEvents, setEditEvents] = useState([]);
  const [editPicked, setEditPicked] = useState([]);
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [sizing, setSizing] = useState('');   // grant id being measured
  // How much of the partner's database this grant covers: their whole history, or
  // only the events actually being co-promoted. `all` is the default because it is
  // what the feature meant before the choice existed — but the picker sits right
  // there, so narrowing is one tap rather than a follow-up conversation.
  const [reach, setReach] = useState('all');           // 'all' | 'events'
  const [events, setEvents] = useState([]);            // the partner's events, once looked up
  const [pickedEvents, setPickedEvents] = useState([]);
  const [eventsBusy, setEventsBusy] = useState(false);
  const [eventsErr, setEventsErr] = useState('');
  // Their events whose names carry Looker filter syntax (a comma, a quote): real
  // events that simply can't be scoped to. Shown so their absence is explained
  // rather than looking like missing data.
  const [skipped, setSkipped] = useState([]);

  const load = () => (isAdmin ? api.adminSharedDatabases(entityId) : api.mySharedDatabases(entityId));
  useEffect(() => {
    let alive = true;
    load().then((v) => { if (alive) setView(v); }).catch(() => { if (alive) setDenied(true); });
    return () => { alive = false; };
  }, [entityId, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  // A client whose switch is off simply doesn't see the section (the route 403s).
  if (denied || !view) return null;

  const grants = view.grants || [];
  const fields = view.fields || {};
  const options = view.options || [];
  // Exactly one ticked = this grant is about one database, so it can carry a label
  // and an event scope. Several = each gets its own row, whole-database, named for
  // itself; narrow or rename any of them from its row afterwards.
  const chosen = picked.length === 1 ? picked[0] : '';
  const source = options.find((o) => o.organiser === chosen) || null;
  const q = search.trim().toLowerCase();
  // The Pulse-known list, filtered locally; the warehouse list arrives from the
  // server already filtered. Merged, deduped, Pulse-known first.
  const localHits = options.filter((o) => !q || o.organiser.toLowerCase().includes(q) || (o.entityName || '').toLowerCase().includes(q));
  const mergedOptions = (() => {
    const byName = new Map(localHits.map((o) => [o.organiser, { ...o, filterable: !/[,%^"]/.test(o.organiser) }]));
    for (const o of (found || [])) if (!byName.has(o.organiser)) byName.set(o.organiser, o);
    // Anything already ticked stays visible even when the search moves on.
    for (const name of picked) if (!byName.has(name)) byName.set(name, { organiser: name, entityName: '', filterable: true });
    return [...byName.values()];
  })();

  const reset = () => {
    setPicked([]); setSearch(''); setFound(null); setFindErr(''); setLabel(''); setNote(''); setAdding(false);
    setReach('all'); setEvents([]); setPickedEvents([]); setEventsErr(''); setSkipped([]);
  };

  // Search the warehouse itself. Deliberately on demand rather than on every
  // keystroke: it is a real query, and the Pulse-known list already covers the
  // common case without touching Looker at all.
  const findOrganisers = async () => {
    setFindBusy(true); setFindErr('');
    try {
      const r = await api.adminSharedDatabaseOrganisers(entityId, search.trim());
      setFound(r.organisers || []);
      if (r.error === 'unavailable') setFindErr('Couldn’t reach the data warehouse — showing only the organisers Pulse already knows.');
      else if (!(r.organisers || []).length) setFindErr('No organiser matches that. Check the spelling against the warehouse.');
    } catch { setFindErr('Couldn’t search the warehouse just now.'); }
    setFindBusy(false);
  };
  const togglePick = (name) => setPicked((cur) => {
    const next = cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name];
    // The event scope describes ONE database — drop back to whole-database as soon
    // as this stops being about one.
    if (next.length !== 1) { setReach('all'); setPickedEvents([]); }
    if (next.length === 1 && reach === 'events') loadEvents(next[0]);
    return next;
  });

  // Their events, read live from the warehouse the moment you choose to narrow —
  // real names, so a grant can never be scoped to an event that doesn't exist.
  const loadEvents = async (org) => {
    if (!org) return;
    setEventsBusy(true); setEventsErr('');
    try {
      const r = await api.adminSharedDatabaseEvents(entityId, org);
      setEvents(r.events || []);
      setSkipped(r.skipped || []);
      if (r.error === 'unavailable' || !(r.events || []).length) setEventsErr('Couldn’t read their events just now — save as the whole database, or try again.');
    } catch { setEventsErr('Couldn’t read their events just now — save as the whole database, or try again.'); }
    setEventsBusy(false);
  };
  const pickReach = (v) => {
    setReach(v);
    if (v === 'events' && chosen && !events.length) loadEvents(chosen);
  };
  const toggleEvent = (name) => setPickedEvents((cur) => (cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]));

  const link = async () => {
    if (!picked.length) { setError('Pick at least one organiser.'); return; }
    if (reach === 'events' && !pickedEvents.length) { setError('Pick at least one event, or share their whole database.'); return; }
    setBusy(true); setError('');
    try {
      const r = await api.adminGrantSharedDatabase(entityId, {
        organisers: picked,
        // Blank = the server names it after the ORGANISER. Never default to the
        // owning client's name: a database belongs to the organiser, and "Shared ·
        // Music People" for Keinemusik's people is actively misleading in the
        // audience builder, where that label is all the client sees.
        label: chosen ? label.trim() : '',
        sourceEntityId: source ? source.entityId : '',
        note: note.trim(),
        events: chosen && reach === 'events' ? pickedEvents : [],
      });
      setView((v) => ({ ...v, grants: r.grants }));
      // Some may not have landed (already linked, or the client's own) — say which
      // rather than quietly showing a shorter list than was ticked.
      const skippedOut = r.skipped || [];
      reset();
      if (skippedOut.length) setError(`Linked ${(r.added || []).length}. Skipped: ${skippedOut.map((x) => `“${x.organiser}” (${x.reason})`).join(', ')}.`);
    } catch (e) { setError(e.message || 'Could not link that database.'); }
    setBusy(false);
  };

  const openScope = async (g) => {
    setEditing(g.id); setEditPicked(g.events || []); setEditEvents([]); setEditErr(''); setEditBusy(true);
    try {
      const r = await api.adminSharedDatabaseEvents(entityId, g.organiser);
      setEditEvents(r.events || []);
      if (!(r.events || []).length) setEditErr('Couldn’t read their events — the link stays as it is.');
    } catch { setEditErr('Couldn’t read their events just now.'); }
    setEditBusy(false);
  };
  const saveScope = async (g) => {
    setEditBusy(true); setEditErr('');
    try {
      const r = await api.adminUpdateSharedDatabase(entityId, g.id, { events: editPicked });
      setView((v) => ({ ...v, grants: r.grants }));
      setEditing(null);
    } catch (e) { setEditErr(e.message || 'Could not change the scope.'); }
    setEditBusy(false);
  };

  // Measure one link's reach. Deliberately a tap rather than automatic on load: a
  // client with eight links shouldn't fire eight warehouse queries to open a page.
  const checkSize = async (g) => {
    setSizing(g.id); setError('');
    try {
      const r = await api.adminSharedDatabaseSize(entityId, g.id);
      setView((v) => ({ ...v, grants: (v.grants || []).map((x) => (x.id === g.id ? r.grant : x)) }));
    } catch (e) { setError(e.message || 'Could not measure that database.'); }
    setSizing('');
  };

  const revoke = async (g) => {
    // Irreversible for anything built on it (the segments empty out on the next
    // read), so make the reader say the name out loud rather than tap through.
    if (!window.confirm(`Revoke access to “${g.label}”?\n\nAny segment this client built from it will resolve to nobody from the next read. Nothing is deleted on the partner's side.`)) return;
    setBusy(true); setError('');
    try {
      const r = await api.adminRevokeSharedDatabase(entityId, g.id);
      setView((v) => ({ ...v, grants: r.grants }));
    } catch (e) { setError(e.message || 'Could not revoke that database.'); }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: isAdmin ? 0 : 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="database" size={16} /> Shared customer databases</h3>
        {view.flagOn === false && <span style={offPill}>Feature off for this client</span>}
      </div>
      <p style={hint}>
        {isAdmin
          ? 'Link another organiser’s customer database to this client — a co-promoter, venue partner or festival they share a fan base with. It unlocks that database for ONE purpose: building a segment, and you choose whether it covers their whole database or only the events being co-promoted.'
          : 'Customer databases another organiser has agreed to share with you. You can build audiences from them — Howler sets up (and removes) the link, and decides how much of each one you can reach.'}
      </p>

      {/* What a link does and does not give — the same words on both surfaces, read
          from the server so the UI can never overstate the grant. */}
      <div style={box}>
        <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>
          <div><b>What you get:</b> {(fields.people || []).join(' · ')} — for <b>everyone</b> the partner holds, not only the people who paid. Used to send, never listed on screen.</div>
          <div style={{ marginTop: 4 }}><b>What you can filter by:</b> {(fields.filters || []).join(' · ')}.</div>
          <div style={{ marginTop: 4, color: 'var(--muted)' }}><b>What stays private:</b> {(fields.never || []).join(' · ')}. Dashboards, the Owl, goals and reporting are untouched — this is the audience only.</div>
        </div>
      </div>

      {grants.length === 0 ? (
        <p style={{ ...hint, marginTop: 10 }}>
          {isAdmin ? 'No shared databases linked yet.' : 'No shared databases yet. Ask your Howler account manager if a partner organiser has agreed to share theirs.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {grants.map((g) => (
            <div key={g.id} style={row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{g.label}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, wordBreak: 'break-word' }}>
                  Organiser: <code>{g.organiser}</code>
                  {isAdmin && g.grantedBy ? ` \u00b7 linked by ${g.grantedBy}` : ''}
                  {g.grantedAt ? ` \u00b7 ${String(g.grantedAt).slice(0, 10)}` : ''}
                </div>
                {/* How much of their database this covers — the thing most worth
                    being able to read at a glance on both surfaces. */}
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>
                  {(g.events || []).length
                    ? <><b>{g.events.length} event{g.events.length === 1 ? '' : 's'} only:</b> <span style={{ color: 'var(--muted)' }}>{g.events.join(' \u00b7 ')}</span></>
                    : <span style={{ color: 'var(--muted)' }}>Their whole database — everyone, every event.</span>}
                </div>
                {/* How many people it actually reaches, and on which channel — the
                    question everyone asks first about a linked database. */}
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>
                  {(g.size?.total ?? -1) >= 0 ? (
                    <>
                      <b>{g.size.total.toLocaleString()} people</b>
                      <span style={{ color: 'var(--muted)' }}>
                        {' '}· {g.size.email.toLocaleString()} emailable · {g.size.sms.toLocaleString()} SMS-reachable
                        {g.size.at ? ` · measured ${String(g.size.at).slice(0, 10)}` : ''}
                      </span>
                      {isAdmin && <button style={{ ...mini, padding: '2px 8px', minHeight: 0, marginLeft: 8 }} disabled={!!sizing} onClick={() => checkSize(g)}>{sizing === g.id ? 'Counting…' : '↻'}</button>}
                      {/* Both populations, because they differ in REACHABILITY as
                          well as size: a warehouse often holds a mobile for the
                          buyer on the order and none for the guests their tickets
                          went to. Without this, "0 SMS-reachable" looks like a bug
                          rather than a choice of audience. */}
      {Object.keys(g.size.byIdentity || {}).length > 0 && (
                        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Whatever populations the data catalogue declares — so a
                              newly added one shows up here (with how many people it
                              finds, or that its explore isn't in this data) without
                              a change on this side. */}
                          {Object.entries(g.size.byIdentity).map(([key, d]) => (
                            <div key={key} style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                              {d.label || key}: {d.error
                                ? <i>not available in this data</i>
                                : <>{d.total.toLocaleString()} people · {d.email.toLocaleString()} email · {d.sms.toLocaleString()} SMS</>}
                            </div>
                          ))}
                          {g.size.byIdentity?.holders?.sms > 0 && (
                            <div style={{ color: 'var(--muted)', fontSize: 11.5, fontStyle: 'italic' }}>
                              A ticket holder’s mobile comes from their own buyer record — so the SMS figure covers
                              holders who have also bought under this organiser.
                            </div>
                          )}
                          {g.size.byIdentity?.holders && g.size.byIdentity?.purchasers
                            && !g.size.byIdentity.holders.error && !g.size.byIdentity.purchasers.error
                            && g.size.byIdentity.holders.sms === 0 && g.size.byIdentity.purchasers.sms > 0 && (
                            <div style={{ color: 'var(--muted)', fontSize: 11.5, fontStyle: 'italic' }}>
                              No mobile is recorded against a ticket holder here, and none of them has bought under
                              this organiser either — so only <b>Buyers</b> is SMS-reachable for this database.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : isAdmin ? (
                    <button style={{ ...mini, padding: '4px 10px', minHeight: 32 }} disabled={!!sizing} onClick={() => checkSize(g)}>
                      {sizing === g.id ? 'Counting…' : 'Check audience size'}
                    </button>
                  ) : <span style={{ color: 'var(--muted)' }}>Size not measured yet — build an audience from it to see the live count.</span>}
                </div>
                {isAdmin && g.note && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>{g.note}</div>}
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button style={mini} disabled={busy} onClick={() => (editing === g.id ? setEditing(null) : openScope(g))}>
                    {editing === g.id ? 'Close' : 'Events'}
                  </button>
                  <button style={revokeBtn} disabled={busy} onClick={() => revoke(g)}>Revoke</button>
                </div>
              )}
              {isAdmin && editing === g.id && (
                <div style={{ ...box, marginTop: 8, flexBasis: '100%' }}>
                  <div style={{ ...hint, marginTop: 0 }}>
                    Tick the events this link should cover, or leave all unticked for their <b>whole database</b>.
                    Changing it re-scopes every segment built on this link at the next read.
                  </div>
                  {editBusy && !editEvents.length ? <div style={hint}>Reading their events…</div> : (
                    <>
                      <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
                        {editEvents.map((name) => (
                          <label key={name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 4px', minHeight: 40, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.35 }}>
                            <input type="checkbox" checked={editPicked.includes(name)}
                              onChange={() => setEditPicked((c) => (c.includes(name) ? c.filter((x) => x !== name) : [...c, name]))} style={{ marginTop: 2 }} />
                            <span style={{ wordBreak: 'break-word' }}>{name}</span>
                          </label>
                        ))}
                      </div>
                      {editErr && <div style={{ ...hint, color: 'var(--danger,#dc2626)' }}>{editErr}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <button style={primaryBtn} disabled={editBusy} onClick={() => saveScope(g)}>
                          {editPicked.length ? `Cover ${editPicked.length} event${editPicked.length === 1 ? '' : 's'}` : 'Cover their whole database'}
                        </button>
                        <button style={mini} disabled={editBusy} onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isAdmin && (adding ? (
        <div style={{ ...box, marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={lbl}>Whose database?</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              style={{ ...input, flex: '1 1 200px' }}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); findOrganisers(); } }}
              placeholder="Search organisers by name…"
            />
            <button type="button" style={mini} disabled={findBusy} onClick={findOrganisers}>
              {findBusy ? 'Searching…' : 'Search all organisers'}
            </button>
          </div>
          <div style={hint}>
            The list below is every organiser Pulse already scopes a client to. <b>Search all organisers</b> looks the
            name up in the data warehouse itself — use it when the partner isn’t a Pulse client, so you pick the exact
            spelling instead of typing it. A database can be linked to <b>any number of clients</b>: one client using
            it never takes it away from another.
          </div>
          {findErr && <div style={{ ...hint, color: 'var(--warn, var(--muted))' }}>{findErr}</div>}
          <div style={{ ...box, marginTop: 0, maxHeight: 260, overflowY: 'auto' }}>
            {mergedOptions.length === 0
              ? <div style={hint}>No organiser matches “{search}”. Try <b>Search all organisers</b>.</div>
              : mergedOptions.map((o) => (
                <label key={o.organiser}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 4px', minHeight: 40, fontSize: 12.5, lineHeight: 1.35, cursor: o.filterable === false ? 'not-allowed' : 'pointer', opacity: o.filterable === false ? 0.5 : 1 }}>
                  <input type="checkbox" disabled={o.filterable === false} checked={picked.includes(o.organiser)} onChange={() => togglePick(o.organiser)} style={{ marginTop: 2 }} />
                  <span style={{ wordBreak: 'break-word' }}>
                    <b>{o.organiser}</b>
                    {/* Who else holds this database. A database is not exclusive —
                        several clients can be linked to the same organiser — so the
                        owners are informational, never a reason it can't be picked. */}
                    {(o.owners || []).length
                      ? <span style={{ color: 'var(--muted)' }}> — {o.owners.join(', ')}</span>
                      : o.entityName
                        ? <span style={{ color: 'var(--muted)' }}> — {o.entityName}</span>
                        : <span style={{ color: 'var(--muted)' }}> — not a Pulse client</span>}
                    {o.isOwn && (
                      <span style={{ display: 'block', color: 'var(--muted)', fontSize: 11.5 }}>
                        Already one of this client’s own organisers — linking it makes it
                        <b> separately selectable</b> in the audience builder, instead of being merged into
                        “Your own customers”.
                      </span>
                    )}
                    {/* What actually tells two near-identical names apart. Picking
                        the wrong "Soul Cand…" silently shares the wrong people. */}
                    {o.organiserId && <span style={{ color: 'var(--muted)' }}> · ID {o.organiserId}</span>}
                    {(o.recentEvents || []).length > 0 && (
                      <span style={{ display: 'block', color: 'var(--muted)', fontSize: 11.5 }}>
                        Latest: {o.recentEvents.join(' · ')}
                      </span>
                    )}
                    {o.filterable === false && <span style={{ display: 'block', color: 'var(--muted)' }}>Can’t be shared — the name contains a character a data filter reads as syntax.</span>}
                  </span>
                </label>
              ))}
          </div>
          {picked.length > 1 && (
            <div style={hint}>
              <b>{picked.length} organisers</b> — each becomes its own link covering their <b>whole</b> database.
              Narrow any one of them to specific events from its row afterwards. Link them one at a time if you want
              to set an event scope or a name as you go.
            </div>
          )}
          {chosen && (
            <>
              <label style={lbl}>What should the client call it?</label>
              <input style={input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={chosen} />
              <div style={hint}>Leave blank to call it <b>{chosen}</b> — the organiser whose database this is.</div>
            </>
          )}
          {chosen && <label style={lbl}>How much of their database?</label>}
          {chosen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[['all', 'Their whole database', 'Everyone they hold, across every event they have ever run.'],
              ['events', 'Only certain events of theirs', 'Just the people from the events you pick \u2014 the co-promoted ones.']].map(([key, title, blurb]) => (
                <button key={key} type="button" onClick={() => pickReach(key)} aria-pressed={reach === key}
                  style={{
                    ...mini, width: '100%', textAlign: 'left', whiteSpace: 'normal', lineHeight: 1.35, padding: '9px 12px',
                    borderColor: reach === key ? 'var(--brand)' : 'var(--hairline)', borderWidth: reach === key ? 2 : 1,
                  }}>
                  <span style={{ fontWeight: 700 }}>{reach === key ? '\u25c9' : '\u25cb'} {title}</span>
                  <span style={{ display: 'block', fontWeight: 500, color: 'var(--muted)', fontSize: 11.5 }}>{blurb}</span>
                </button>
            ))}
          </div>
          )}
          {chosen && reach === 'events' && (
            <div style={{ ...box, marginTop: 0 }}>
              {eventsBusy ? <div style={hint}>Reading their events…</div>
                  : events.length ? (
                    <>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{pickedEvents.length} of {events.length} picked</span>
                        <button type="button" style={{ ...mini, padding: '4px 10px', minHeight: 32 }} onClick={() => setPickedEvents([])}>Clear</button>
                        <button type="button" style={{ ...mini, padding: '4px 10px', minHeight: 32 }} onClick={() => loadEvents(chosen)}>↻ Refresh</button>
                      </div>
                      <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {events.map((name) => (
                          <label key={name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 4px', minHeight: 40, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.35 }}>
                            <input type="checkbox" checked={pickedEvents.includes(name)} onChange={() => toggleEvent(name)} style={{ marginTop: 2 }} />
                            <span style={{ wordBreak: 'break-word' }}>{name}</span>
                          </label>
                        ))}
                      </div>
                      {skipped.length > 0 && (
                        <div style={{ ...hint, marginTop: 8 }}>
                          {skipped.length} of their events can’t be picked — their names contain a character
                          (a percent sign or a quote) that a data filter reads as syntax, so scoping to them would
                          quietly match nobody: <b>{skipped.join(' · ')}</b>. Share the whole database to include them.
                        </div>
                      )}
                    </>
                  ) : <div style={hint}>{eventsErr || 'No events found for that organiser.'}</div>}
              {eventsErr && events.length > 0 && <div style={{ ...hint, color: 'var(--warn, var(--muted))' }}>{eventsErr}</div>}
            </div>
          )}
          <label style={lbl}>Why is this allowed?</label>
          <textarea
            style={{ ...input, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Co-promoted 2026 tour — joint marketing agreement signed 12 Mar. (Internal only; the client never sees this.)"
          />
          <div style={hint}>
            Recorded with your name in the audit trail. Make sure the sharing organiser has actually agreed and that their customers’ consent covers it —
            this hands their fans’ contact details to another client’s campaigns.
          </div>
          {error && <div style={errS}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={primaryBtn} disabled={busy || !chosen} onClick={link}>{busy ? 'Linking…' : 'Link this database'}</button>
            <button style={mini} disabled={busy} onClick={reset}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {error && <div style={{ ...errS, marginBottom: 8 }}>{error}</div>}
          <button style={primaryBtn} onClick={() => { setAdding(true); setError(''); }}>＋ Link a shared database</button>
        </div>
      ))}

      {!isAdmin && grants.length > 0 && (
        <p style={{ ...hint, marginTop: 10 }}>
          Build one in <b>Engage → Segments → ＋ New segment → Build an audience</b>, then pick the shared database as the source.
          To add or remove a database, speak to your Howler account manager.
        </p>
      )}
    </div>
  );
}

const hint = { fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, margin: '6px 0 0' };
const lbl = { fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)' };
const box = { border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', marginTop: 10, background: 'var(--card)' };
const row = { display: 'flex', alignItems: 'flex-start', gap: 10, border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', flexWrap: 'wrap' };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', minHeight: 40, border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--text)' };
const primaryBtn = { padding: '10px 14px', minHeight: 40, borderRadius: 8, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const mini = { padding: '10px 12px', minHeight: 40, borderRadius: 8, border: '1.5px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const revokeBtn = { ...mini, color: 'var(--danger,#dc2626)', borderColor: 'var(--danger,#dc2626)' };
const offPill = { fontSize: 10.5, fontWeight: 800, borderRadius: 980, padding: '2px 9px', background: 'rgba(220,38,38,0.12)', color: 'var(--danger,#dc2626)' };
const errS = { fontSize: 12.5, color: 'var(--danger,#dc2626)', fontWeight: 600 };
